import { formatDate } from '@frontend/mwm-ui'
import classNames from 'classnames'
import { useState } from 'react'
import { FaUnity } from 'react-icons/fa'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'

import type { ReactElement, MouseEvent } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IPageContainer } from '~/types/page-container'

import PageContainerDefaultPreview from '~/assets/page-container-default-preview.svg'
import AppLogo from '~/components/AppLogo'
import Avatar from '~/components/Avatar'
import Badge from '~/components/Badge'
import { PageContainerBadge, TagBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import DropdownMenu from '~/components/DropdownMenu'
import Tooltip from '~/components/Tooltip'
import { useUserDetailsQuery } from '~/queries/users'
import dayjs from '~/utils/dayjs'

import './styles.scss'

const MAX_TAG_ITEMS = 2

interface Props {
  appLogoInfo?: IAppSummary
  className?: string
  menu?: {
    items: {
      label: string
      onClick?: VoidFunction
    }[]
    sectionTitle?: string
  }[]
  onClick?: VoidFunction
  pageContainer: IPageContainer
  showSubInfos?: boolean
}

const NoItems = () => <span className="page-container-card-component__information__no-items">--</span>

const PageContainerCard = ({
  appLogoInfo,
  className,
  menu,
  onClick,
  pageContainer,
  showSubInfos,
}: Props): ReactElement => {
  /** Local state */

  const {
    capabilities,
    is_unity,
    navigation_target_outputs: targets,
    requirements,
    status,
    title,
    updated_at: updatedAt,
    updated_by: updatedBy,
  } = pageContainer

  const classes = classNames('page-container-card-component', className)

  const [previewUrlIndex, setPreviewUrlIndex] = useState(0)

  const now = dayjs(Date.now())

  const dayDiff = now.diff(updatedAt, 'days')

  const { screenshot_file_urls } = pageContainer

  const screenshotFileUrls = screenshot_file_urls || []

  const previewUrl = screenshotFileUrls.length > 0 ? screenshotFileUrls[previewUrlIndex] : PageContainerDefaultPreview

  /** Queries & Mutations */

  const { data: user } = useUserDetailsQuery(updatedBy)

  /** Handlers */

  const handlePrevious = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation()
    setPreviewUrlIndex((index) => {
      if (index > 0) {
        return index - 1
      }
      return screenshotFileUrls.length - 1
    })
  }

  const handleNext = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation()
    setPreviewUrlIndex((index) => {
      if (index < screenshotFileUrls.length - 1) {
        return index + 1
      }
      return 0
    })
  }

  return (
    <div className={classes}>
      {!!menu ? (
        <DropdownMenu className="dropdown-menu" menu={menu} onMouseDown={(e: MouseEvent) => e.stopPropagation()} />
      ) : null}
      {status && (
        <div className="card-menu-container">
          <PageContainerBadge value={status} />
        </div>
      )}
      <div
        className={classNames('page-container-card-component__preview', {
          'with-pagination': screenshotFileUrls.length > 0,
        })}
        onClick={onClick}
      >
        <div className="preview-pagination__text">
          {previewUrlIndex + 1}/{screenshotFileUrls.length}
        </div>
        <Button className="preview-pagination__button" ghost onClick={handlePrevious} size="small" type="button">
          <MdKeyboardArrowLeft color="#aeaeae" />
        </Button>
        <img src={previewUrl} />
        <Button className="preview-pagination__button" ghost onClick={handleNext} size="small" type="button">
          <MdKeyboardArrowRight color="#aeaeae" />
        </Button>
        <AppLogo
          appLogoUrl={appLogoInfo?.app_icon_url}
          appPlatform={appLogoInfo?.device_type}
          className="app-logo"
          size="medium"
        />
        {is_unity ? <FaUnity className="is-unity-icon" size={16} /> : null}
      </div>
      <div className="page-container-card-component__information">
        <div className="page-container-card-component__information__name">{title}</div>
        {showSubInfos && (
          <>
            {updatedAt && (
              <div className="page-container-card-component__information__edit-date">
                Edited{' '}
                {dayDiff === 0
                  ? 'a few hours ago'
                  : dayDiff > 3
                    ? `on ${formatDate(updatedAt)}`
                    : `${dayDiff} day${dayDiff > 1 ? 's' : ''} ago`}
                {user && <Avatar className="avatar" pictureUrl={user.picture} />}
              </div>
            )}
            <div className="page-container-card-component__information__capabilities">
              <span className="page-container-card-component__information__title">Capabilities:</span>
              {capabilities && capabilities.length > 0 ? (
                <>
                  {capabilities.slice(0, MAX_TAG_ITEMS).map((capability, i) => (
                    <TagBadge
                      className="badge capability-badge"
                      key={`${capabilities}-${i}`}
                      size="medium"
                      value={capability}
                    />
                  ))}
                  {capabilities.length > MAX_TAG_ITEMS ? (
                    <Tooltip
                      align={{ points: ['br', 'tr'] }}
                      overlay={
                        <div className="page-container-tooltips-content">
                          {capabilities.slice(MAX_TAG_ITEMS).map((capability, i) => (
                            <TagBadge
                              className="badge capability-badge badge"
                              key={`tooltip-${capabilities}-${i}`}
                              size="medium"
                              value={capability}
                            />
                          ))}
                        </div>
                      }
                      placement="top"
                      trigger="hover"
                    >
                      <Badge className="counter" value={`+${capabilities.length - MAX_TAG_ITEMS}`} />
                    </Tooltip>
                  ) : null}
                </>
              ) : (
                <NoItems />
              )}
            </div>
            <div className="page-container-card-component__information__targets">
              <span className="page-container-card-component__information__title">Nav. targets:</span>
              {targets && targets.length > 0 ? (
                <>
                  {targets.slice(0, MAX_TAG_ITEMS).map((target, i) => (
                    <TagBadge className="badge target-badge" key={`${target}-${i}`} size="medium" value={target} />
                  ))}
                  {targets.length > MAX_TAG_ITEMS ? (
                    <Tooltip
                      align={{ points: ['br', 'tr'] }}
                      overlay={
                        <div className="page-container-tooltips-content">
                          {targets.slice(MAX_TAG_ITEMS).map((target, i) => (
                            <TagBadge
                              className="badge target-badge badge"
                              key={`tooltip-${target}-${i}`}
                              size="medium"
                              value={target}
                            />
                          ))}
                        </div>
                      }
                      placement="top"
                      trigger="hover"
                    >
                      <Badge className="counter" value={`+${targets.length - MAX_TAG_ITEMS}`} />
                    </Tooltip>
                  ) : null}
                </>
              ) : (
                <span className="page-container-card-component__information__no-items">--</span>
              )}
            </div>
            <div className="page-container-card-component__information__requirements">
              <span className="page-container-card-component__information__title">Requirements:</span>
              {requirements && requirements.length > 0 ? (
                <>
                  {requirements.slice(0, MAX_TAG_ITEMS).map((requirement, i) => (
                    <TagBadge
                      className="badge requirement-badge"
                      key={`${requirement}-${i}`}
                      size="medium"
                      value={requirement}
                    />
                  ))}
                  {requirements.length > MAX_TAG_ITEMS ? (
                    <Tooltip
                      align={{ points: ['br', 'tr'] }}
                      overlay={
                        <div className="page-container-tooltips-content">
                          {requirements.slice(MAX_TAG_ITEMS).map((requirement, i) => (
                            <TagBadge
                              className="badge requirement-badge badge"
                              key={`tooltip-${requirement}-${i}`}
                              size="medium"
                              value={requirement}
                            />
                          ))}
                        </div>
                      }
                      placement="top"
                      trigger="hover"
                    >
                      <Badge className="counter" value={`+${requirements.length - MAX_TAG_ITEMS}`} />
                    </Tooltip>
                  ) : null}
                </>
              ) : (
                <NoItems />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PageContainerCard
